// =============================================================
// Widget modifier runtime — wires IntersectionObserver for the
// scroll-triggered modifiers (fade-in, slide-up, blur-in, etc.)
// and applies CSS classes for the static ones (hover-lift, tilt).
// =============================================================

import type { WidgetModifier } from './widget-config';

export function getModifierClass(modifier: WidgetModifier): string {
  if (!modifier || modifier === 'none') return '';
  return `sloth-mod-${modifier}`;
}

let observer: IntersectionObserver | null = null;

/** Lazy-init a single shared IntersectionObserver for all scroll
 * modifiers. We attach the observer to the document so it sees every
 * widget, regardless of where in the page they live. */
function getObserver(): IntersectionObserver {
  if (observer) return observer;
  observer = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (e.isIntersecting) {
        e.target.classList.add('is-visible');
        // Once visible, stop watching — the animation only runs once.
        observer!.unobserve(e.target);
      }
    }
  }, { threshold: 0.1, rootMargin: '0px 0px -10% 0px' });
  return observer;
}

const SCROLL_TRIGGERS: WidgetModifier[] = [
  'fade-in', 'slide-up', 'blur-in', 'scale-on-scroll',
];

/** Attach the modifier class + scroll-observer to a DOM element. */
export function applyModifier(element: HTMLElement, modifier: WidgetModifier): void {
  const cls = getModifierClass(modifier);
  if (!cls) return;
  element.classList.add(cls);
  if (SCROLL_TRIGGERS.includes(modifier)) {
    getObserver().observe(element);
  }
}

/** Apply modifiers to every widget root in a subtree. */
export function applyModifiers(root: ParentNode, modifier: WidgetModifier): void {
  const targets = root.querySelectorAll<HTMLElement>('[data-widget]');
  targets.forEach(el => applyModifier(el, modifier));
}
